import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginUser as loginThunk, clearError } from '../../redux/authentication/utils/core/authslice';
import { User, Lock, ArrowLeft, Clock, ShieldCheck, Sun, Moon } from 'lucide-react';
import { Input, Button } from '../../components/ui';
import { useTheme } from '../../theme/ThemeContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const { t } = useTranslation();
  const LOADING_MESSAGES = t('auth.loadingMessages', { returnObjects: true });
  const [username, setUsername]       = useState(localStorage.getItem('last-email') || '');
  const [password, setPassword]       = useState('');
  const [localError, setLocalError]   = useState('');
  const [cooldown, setCooldown]       = useState(0);
  const [loadingStep, setLoadingStep] = useState(0);
  const [capsLockOn, setCapsLockOn]   = useState(false);
  const [touched, setTouched]         = useState({ email: false, password: false });

  const emailRef    = useRef(null);
  const passwordRef = useRef(null);
  // 2026-08-11: the only guard against a double-submit used to be Redux's
  // `loading` flag driving the submit button's disabled state -- but that
  // requires a full dispatch+re-render round trip to take effect, leaving a
  // real window (Enter-key submit landing right beside a click, browser
  // autofill auto-submitting alongside a manual submit, or just a fast
  // double-click) where a second handleSubmit call fires before the button
  // visually disables. The second call would then hit authService.js's own
  // client-side single-flight/1s-cooldown guard (_checkOperationState) and
  // throw synchronously -- landing in Redux `error` state and being shown
  // to the user (via handleAuthError's generic fallback) as "Email or
  // password is incorrect" well before the FIRST, real request's slower
  // network round trip even resolves. The real request then succeeds
  // moments later on its own, which is exactly why a manual retry "just
  // works" right after -- reproduced and root-caused live, not guessed.
  // A ref (not state) is required here specifically because it updates
  // synchronously, with no re-render in between -- a useState flag would
  // have the identical race the Redux `loading` flag already has.
  const submitInFlightRef = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error: authError, mfaRequired, mfaSetupRequired } = useSelector((s) => s.auth);
  const { isDark, toggleTheme } = useTheme();

  /* ------------------ AUTH FLOW ------------------ */

  useEffect(() => {
    if (!isAuthenticated) return;
    // MFA_ENFORCEMENT_MODE (admin-configurable): route to the existing MFA
    // setup screen instead of the dashboard when this login requires it.
    // Tokens are already issued normally either way -- this is a redirect
    // only, not a login block.
    if (mfaSetupRequired) {
      navigate('/settings/security', { state: { autoStartMfaSetup: true } });
    } else {
      navigate('/dashboard');
    }
  }, [isAuthenticated, mfaSetupRequired, navigate]);

  useEffect(() => {
    if (mfaRequired) navigate('/login/mfa');
  }, [mfaRequired, navigate]);

  useEffect(() => {
    if (authError) handleAuthError(authError);
  }, [authError]);

  useEffect(() => () => { dispatch(clearError()); }, [dispatch]);

  /* ------------------ LOADING ROTATOR ------------------ */

  useEffect(() => {
    if (!loading) return;
    setLoadingStep(0);
    const id = setInterval(() => setLoadingStep((p) => (p + 1) % LOADING_MESSAGES.length), 1500);
    return () => clearInterval(id);
  }, [loading, LOADING_MESSAGES.length]);

  /* ------------------ COOLDOWN TIMER ------------------ */

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  /* ------------------ HELPERS ------------------ */

  const handleAuthError = (msg) => {
    const match = msg?.match(/(\d+)\s?seconds/i);
    if (match) {
      const secs = parseInt(match[1], 10);
      setCooldown(secs);
      setLocalError(t('auth.login.errors.tooManyAttempts', { seconds: secs }));
      return;
    }
    if (msg?.toLowerCase().includes('network')) {
      setLocalError(t('auth.login.errors.networkError'));
      return;
    }
    // Defense in depth (2026-08-11): authService.js's own client-side
    // single-flight/1s-cooldown guard (_checkOperationState) throws these
    // exact messages for a duplicate/rapid-repeat submit -- a client-side
    // timing issue, not a real credentials failure. submitInFlightRef above
    // should prevent the common trigger before it ever reaches here, but if
    // it ever does (a second browser tab, a genuinely-stuck-then-recovered
    // lock), show what actually happened instead of telling the user their
    // real password is wrong.
    if (/operation already in progress|wait before attempting/i.test(msg || '')) {
      setLocalError(t('auth.login.errors.submitInProgress'));
      return;
    }
    setLocalError(t('auth.login.errors.invalidCredentials'));
    passwordRef.current?.focus();
  };

  const isEmailValid    = EMAIL_REGEX.test(username);
  const isPasswordValid = password.length > 0;

  /* ------------------ SUBMIT ------------------ */

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitInFlightRef.current) return; // silently ignore a near-simultaneous duplicate submit
    setTouched({ email: true, password: true });
    if (!isEmailValid)    { setLocalError(t('auth.login.errors.invalidEmail')); return; }
    if (!isPasswordValid) { setLocalError(t('auth.login.errors.passwordRequired')); return; }
    submitInFlightRef.current = true;
    try {
      localStorage.setItem('last-email', username);
      await dispatch(loginThunk({ username, password })).unwrap();
    } catch {
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const handleKeyPress = (e) => setCapsLockOn(e.getModifierState('CapsLock'));

  return (
    <>
      <div
        className="min-h-screen w-full flex items-center justify-center px-4 py-12 relative overflow-hidden bg-gray-50 dark:bg-[#09090f]"
        style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>

        {/* Top radial glow — identical to the homepage hero. Dark-only by
            design (a light-mode wash of the same violet at this size/opacity
            reads as a stain on a white background, not a glow) -- CSS
            handles the toggle via the dark: variant rather than JS. */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[480px] pointer-events-none hidden dark:block"
          style={{ background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)' }}
        />

        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 flex items-center text-violet-600 dark:text-violet-300 hover:text-violet-500 dark:hover:text-violet-100 transition-colors z-50"
        >
          <ArrowLeft className="w-5 h-5 mr-1" /> {t('auth.back')}
        </button>

        <button
          onClick={toggleTheme}
          className="absolute top-6 right-6 p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors z-50"
          title={isDark ? t('auth.switchToLight') : t('auth.switchToDark')}
          aria-label={isDark ? t('auth.switchToLight') : t('auth.switchToDark')}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        <div className="relative w-full max-w-md z-10">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img src="/StockerLogo.png" className="h-16" alt="Stocker" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.login.heading')}</h2>
            <p className="text-gray-600 dark:text-gray-400">{t('auth.login.subtitle')}</p>
          </div>

          {/* Card */}
          <div className="rounded-3xl p-8 shadow-2xl bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]">
            <form className="space-y-6" onSubmit={handleSubmit}>

              <Input
                ref={emailRef}
                icon={User}
                type="email"
                autoComplete="username"
                placeholder={t('auth.login.emailPlaceholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                error={touched.email && !isEmailValid}
              />

              <div>
                <Input
                  ref={passwordRef}
                  icon={Lock}
                  isPassword
                  autoComplete="current-password"
                  placeholder={t('auth.login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={handleKeyPress}
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  error={touched.password && !isPasswordValid}
                />
                {/* Caps Lock */}
                {capsLockOn && (
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs mt-1.5">
                    <Clock size={14} /> {t('auth.login.capsLockOn')}
                  </div>
                )}
              </div>

              {/* Forgot */}
              <div className="text-right text-sm">
                <Link to="/forgot-password" className="text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300 transition-colors">
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>

              {/* Error / cooldown */}
              {localError && (
                <div className={`p-3 rounded-lg text-sm border ${
                  cooldown > 0
                    ? 'bg-amber-50 dark:bg-amber-600/20 border-amber-300 dark:border-amber-400 text-amber-800 dark:text-amber-200'
                    : 'bg-red-50 dark:bg-red-600/30 border-red-300 dark:border-red-500 text-red-700 dark:text-red-200'
                }`}>
                  {localError}
                </div>
              )}

              {/* Loading message */}
              {loading && (
                <div className="text-center text-violet-700 dark:text-violet-200 text-sm font-medium animate-pulse">
                  {LOADING_MESSAGES[loadingStep]}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={cooldown > 0}
              >
                {loading ? t('auth.login.signingIn') : cooldown > 0 ? t('auth.login.retryIn', { seconds: cooldown }) : t('auth.login.signIn')}
              </Button>

              {/* Security note */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
                <ShieldCheck size={14} /> {t('auth.login.secureNote')}
              </div>

            </form>

            <div className="mt-6 text-center text-gray-600 dark:text-gray-400 text-sm">
              {t('auth.login.noAccount')}{' '}
              <Link to="/register" className="text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300 font-medium transition-colors">
                {t('auth.login.createOne')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;